import { Text } from '@customerio/pluma-components/react';
import { isTwoValueFilterOperator, isUnaryFilterOperator } from '../../attribute-filter-state';
import {
  isAbsoluteTimestampOperator,
  isRelativeTimestampOperator,
  isTimestampInequalityOperator,
} from '../../relative-timestamp';
import { AbsoluteTimestampValue, RelativeTimestampValue, TimestampRangeValue } from './timestamp-editors';
import { ValueCombobox } from './value-combobox';
import type { OperatorValuePatch, ValueOption } from './types';

export type ValueEditorProps = {
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  disabled?: boolean;
  valueError?: string;
  onChange: (patch: OperatorValuePatch) => void;
  onValueBlur: () => void;
};

/**
 * The value half of a condition row — whichever editor the operator needs.
 *
 * A unary operator (`exists`) renders nothing: it takes no value, and the one
 * it may carry off the wire is never shown.
 */
export function ValueEditor({
  field,
  operator,
  value,
  value2,
  valueOptions,
  isLoadingValueOptions,
  disabled,
  valueError,
  onChange,
  onValueBlur,
}: ValueEditorProps) {
  if (isUnaryFilterOperator(operator)) {
    return null;
  }

  if (operator === 'timestamp_bw') {
    return <TimestampRangeValue disabled={disabled} value={value} value2={value2} onChange={onChange} />;
  }

  if (isRelativeTimestampOperator(operator)) {
    return (
      <RelativeTimestampValue disabled={disabled} field={field} operator={operator} value={value} onChange={onChange} />
    );
  }

  if (isAbsoluteTimestampOperator(operator)) {
    return <AbsoluteTimestampValue disabled={disabled} value={value} onChange={onChange} />;
  }

  // A timestamp inequality with no variant resolved yet renders only its
  // variant select, which the operator row owns.
  if (isTimestampInequalityOperator(operator)) {
    return null;
  }

  if (isTwoValueFilterOperator(operator)) {
    return (
      <>
        <ValueCombobox
          ariaLabel="Value"
          testId="dryad-filter-value-input"
          disabled={disabled}
          inputValue={value}
          isLoadingValueOptions={isLoadingValueOptions}
          valueOptions={valueOptions}
          error={valueError}
          onValueChange={(nextValue) => onChange({ value: nextValue })}
          onBlur={onValueBlur}
        />
        <Text as="span" color="subtle">
          and
        </Text>
        <ValueCombobox
          ariaLabel="Second value"
          disabled={disabled}
          inputValue={value2}
          isLoadingValueOptions={isLoadingValueOptions}
          valueOptions={valueOptions}
          onValueChange={(nextValue) => onChange({ value2: nextValue })}
        />
      </>
    );
  }

  return (
    <ValueCombobox
      ariaLabel="Value"
      testId="dryad-filter-value-input"
      disabled={disabled}
      inputValue={value}
      isLoadingValueOptions={isLoadingValueOptions}
      valueOptions={valueOptions}
      error={valueError}
      onValueChange={(nextValue) => onChange({ value: nextValue })}
      onBlur={onValueBlur}
    />
  );
}
